import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  GraduationCap,
  Plus,
  Search,
  Clock,
  CheckCircle,
  Play,
  Award,
  BookOpen,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const courseCategories = [
  'Health & Safety',
  'Medication Administration',
  'Manual Handling',
  'Safeguarding Adults',
  'Safeguarding Children',
  'First Aid',
  'Infection Control',
  'Food Hygiene',
  'Mental Capacity Act',
  'GDPR & Data Protection',
  'Fire Safety',
  'Equality & Diversity',
];

export default function Training() {
  const { user, organization } = useAuth();
  const [courses, setCourses] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    duration_hours: 1,
    is_mandatory: false,
    category: '',
    content_url: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization]);

  const fetchData = async () => {
    try {
      const [coursesRes, recordsRes] = await Promise.all([
        axios.get(`${API_URL}/api/training/courses`, {
          params: { organization_id: organization?.id }
        }),
        axios.get(`${API_URL}/api/training/records`, {
          params: { organization_id: organization?.id }
        })
      ]);
      
      setCourses(coursesRes.data);
      setRecords(recordsRes.data);
    } catch (error) {
      console.error('Error fetching training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/training/courses`, {
        ...newCourse,
        organization_id: organization?.id,
        duration_hours: parseFloat(newCourse.duration_hours),
      });
      toast.success('Training course created successfully');
      setIsDialogOpen(false);
      setNewCourse({
        title: '',
        description: '',
        duration_hours: 1,
        is_mandatory: false,
        category: '',
        content_url: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('Failed to create course');
    }
  };

  const handleStartCourse = async (courseId) => {
    // Get staff record for current user
    try {
      const staffRes = await axios.get(`${API_URL}/api/staff`, {
        params: { organization_id: organization?.id }
      });
      const currentStaff = staffRes.data.find(s => s.user_id === user?.id);
      
      if (!currentStaff) {
        toast.error('Staff record not found');
        return;
      }

      await axios.post(`${API_URL}/api/training/records`, null, {
        params: {
          staff_id: currentStaff.id,
          course_id: courseId,
          organization_id: organization?.id,
        }
      });
      toast.success('Course started!');
      fetchData();
    } catch (error) {
      console.error('Error starting course:', error);
      toast.error('Failed to start course');
    }
  };

  const handleCompleteCourse = async (recordId) => {
    try {
      await axios.put(`${API_URL}/api/training/records/${recordId}/complete`, null, {
        params: { score: 100 }
      });
      toast.success('Course completed!');
      fetchData();
    } catch (error) {
      console.error('Error completing course:', error);
      toast.error('Failed to complete course');
    }
  };

  const filteredCourses = courses.filter(course => {
    return course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           course.category?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getCourseRecord = (courseId) => {
    return records.find(r => r.course_id === courseId);
  };

  const completedCount = records.filter(r => r.status === 'completed').length;
  const mandatoryCourses = courses.filter(c => c.is_mandatory);
  const mandatoryCompleted = mandatoryCourses.filter(c => {
    const record = getCourseRecord(c.id);
    return record?.status === 'completed';
  }).length;

  return (
    <div className="min-h-screen" data-testid="training-page">
      <Header title="Training" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="course-search"
            />
          </div>
          
          {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr') && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-course-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Training Course</DialogTitle>
                  <DialogDescription>
                    Add a new training course for your organization
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCourse} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Course Title *</Label>
                    <Input
                      id="title"
                      value={newCourse.title}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Fire Safety Awareness"
                      required
                      data-testid="course-title"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select 
                        value={newCourse.category}
                        onValueChange={(v) => setNewCourse(prev => ({ ...prev, category: v }))}
                      >
                        <SelectTrigger data-testid="course-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {courseCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duration_hours">Duration (hours) *</Label>
                      <Input
                        id="duration_hours"
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={newCourse.duration_hours}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, duration_hours: e.target.value }))}
                        required
                        data-testid="course-duration"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={newCourse.description}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      placeholder="Describe the course content and objectives..."
                      required
                      data-testid="course-description"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="content_url">Content URL</Label>
                    <Input
                      id="content_url"
                      type="url"
                      value={newCourse.content_url}
                      onChange={(e) => setNewCourse(prev => ({ ...prev, content_url: e.target.value }))}
                      placeholder="https://..."
                      data-testid="course-url"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_mandatory"
                      checked={newCourse.is_mandatory}
                      onCheckedChange={(checked) => setNewCourse(prev => ({ ...prev, is_mandatory: checked }))}
                      data-testid="course-mandatory"
                    />
                    <label htmlFor="is_mandatory" className="text-sm font-medium">
                      This is a mandatory course for all staff
                    </label>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-course">
                      Create Course
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Available Courses</p>
                  <p className="text-3xl font-bold">{courses.length}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-3xl font-bold text-green-600">{completedCount}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Mandatory</p>
                  <p className="text-3xl font-bold">{mandatoryCourses.length}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compliance</p>
                  <p className="text-3xl font-bold text-teal-600">
                    {mandatoryCourses.length > 0 
                      ? Math.round((mandatoryCompleted / mandatoryCourses.length) * 100) 
                      : 100}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-slate-100 animate-pulse rounded-xl" />
            ))
          ) : filteredCourses.length > 0 ? (
            filteredCourses.map((course) => {
              const record = getCourseRecord(course.id);
              return (
                <Card key={course.id} className="overflow-hidden" data-testid={`course-${course.id}`}>
                  <div className={`h-2 ${course.is_mandatory ? 'bg-red-500' : 'bg-teal-500'}`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={course.is_mandatory ? 'destructive' : 'secondary'}>
                          {course.is_mandatory ? 'Mandatory' : 'Optional'}
                        </Badge>
                        {record?.status === 'completed' && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-2">{course.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration_hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.category}
                      </span>
                    </div>
                    
                    {record ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Progress</span>
                          <span className="font-medium">
                            {record.status === 'completed' ? '100%' : record.status === 'in_progress' ? '50%' : '0%'}
                          </span>
                        </div>
                        <Progress 
                          value={record.status === 'completed' ? 100 : record.status === 'in_progress' ? 50 : 0} 
                          className="h-2"
                        />
                        {record.status !== 'completed' && (
                          <Button 
                            className="w-full bg-teal-600 hover:bg-teal-700"
                            onClick={() => handleCompleteCourse(record.id)}
                            data-testid={`complete-${course.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark as Complete
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button 
                        className="w-full"
                        variant="outline"
                        onClick={() => handleStartCourse(course.id)}
                        data-testid={`start-${course.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Course
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12">
              <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No courses found</p>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Create First Course
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
